import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Alert, Button, TextArea } from '../../components/ui'
import { ApiError, ContractError, NetworkError } from '../../api/client'
import { isEvidenceRefusal, verifyListing, VERIFICATION_FACTS, VERIFICATION_METHODS } from '../../api/objects'
import type { VerificationFact, VerificationMethod } from '../../api/objects'
import { keys } from '../../api/keys'
import { t } from '../../i18n'
import { fullTime } from '../../format'
import { verificationFactLabel, verificationMethodLabel } from '../../labels'

/**
 * Recording that somebody checked this offer.
 *
 * Two things the form refuses to do, both from `domain/listing`: a check with no
 * evidence, and a check that says it confirmed nothing. Neither is a formality.
 * Availability is answered from the stored check (ADR 0008), so a check nobody
 * can show anything for is a client told a room is free on the strength of
 * somebody's memory; and a check that does not say what it covered cannot tell
 * the matcher whether the price it is about to quote was the part that was
 * confirmed.
 *
 * No lifetime is offered. How long a check stays good is a setting, not a choice
 * the person making it gets to widen.
 */

function messageFor(error: unknown): string {
  if (isEvidenceRefusal(error)) return t('verify.evidenceRequired')
  if (error instanceof ApiError) return error.message
  if (error instanceof NetworkError) return t('errors.network')
  if (error instanceof ContractError) return t('errors.badResponse')
  return t('verify.failed')
}

export function VerifyPanel({
  propertyId,
  listingId,
}: {
  readonly propertyId: string
  readonly listingId: string
}) {
  const queryClient = useQueryClient()

  const [open, setOpen] = useState(false)
  const [method, setMethod] = useState<VerificationMethod>('owner_message')
  const [evidence, setEvidence] = useState('')
  // Availability is pre-selected because it is what is nearly always being
  // checked — "is the room still free" — and because a form that starts with
  // nothing ticked trains people to tick everything.
  const [facts, setFacts] = useState<readonly VerificationFact[]>(['availability'])
  const [note, setNote] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [recordedUntil, setRecordedUntil] = useState<string | null>(null)

  const record = useMutation({
    mutationFn: verifyListing,
    onSuccess: (result) => {
      setOpen(false)
      setEvidence('')
      setNote('')
      setFailure(null)
      setRecordedUntil(result.verification.expiresAt)
      void queryClient.invalidateQueries({ queryKey: keys.property(propertyId) })
      void queryClient.invalidateQueries({ queryKey: keys.listingHistory(listingId) })
      void queryClient.invalidateQueries({ queryKey: keys.anyPropertyList() })
    },
    onError: (error: unknown) => {
      setFailure(messageFor(error))
    },
  })

  return (
    <VerifyFormView
      open={open}
      method={method}
      evidence={evidence}
      facts={facts}
      note={note}
      busy={record.isPending}
      failure={failure}
      recordedUntil={recordedUntil}
      onOpen={() => {
        setRecordedUntil(null)
        setFailure(null)
        setOpen(true)
      }}
      onCancel={() => setOpen(false)}
      onMethod={setMethod}
      onEvidence={setEvidence}
      onNote={setNote}
      onToggleFact={(fact) =>
        setFacts((current) =>
          current.includes(fact) ? current.filter((entry) => entry !== fact) : [...current, fact],
        )
      }
      onSubmit={() => {
        setFailure(null)
        record.mutate({ listingId, method, evidence, facts, note })
      }}
    />
  )
}

export interface VerifyFormProps {
  readonly open: boolean
  readonly method: VerificationMethod
  readonly evidence: string
  readonly facts: readonly VerificationFact[]
  readonly note: string
  readonly busy: boolean
  readonly failure: string | null
  readonly recordedUntil: string | null
  readonly onOpen: () => void
  readonly onCancel: () => void
  readonly onMethod: (method: VerificationMethod) => void
  readonly onEvidence: (evidence: string) => void
  readonly onNote: (note: string) => void
  readonly onToggleFact: (fact: VerificationFact) => void
  readonly onSubmit: () => void
}

/** The form with the request taken off it, so a refusal can be put on screen. */
export function VerifyFormView({
  open,
  method,
  evidence,
  facts,
  note,
  busy,
  failure,
  recordedUntil,
  onOpen,
  onCancel,
  onMethod,
  onEvidence,
  onNote,
  onToggleFact,
  onSubmit,
}: VerifyFormProps) {
  const noEvidence = evidence.trim() === ''
  const noFacts = facts.length === 0

  if (!open) {
    return (
      <div className="verify">
        {failure === null ? null : <Alert tone="error">{failure}</Alert>}
        {recordedUntil === null ? null : (
          <Alert tone="info">{t('verify.recorded', { time: fullTime(recordedUntil) })}</Alert>
        )}
        <Button variant="secondary" onClick={onOpen}>
          {t('verify.button')}
        </Button>
      </div>
    )
  }

  return (
    <div className="verify confirm">
      <p className="confirm__title">{t('verify.title')}</p>

      {failure === null ? null : <Alert tone="error">{failure}</Alert>}

      <div className="field">
        <label className="field__label" htmlFor="verify-method">
          {t('verify.method')}
        </label>
        <select
          id="verify-method"
          className="field__input"
          value={method}
          disabled={busy}
          onChange={(event) => onMethod(event.target.value as VerificationMethod)}
        >
          {VERIFICATION_METHODS.map((option) => (
            <option key={option} value={option}>
              {verificationMethodLabel(option)}
            </option>
          ))}
        </select>
      </div>

      <TextArea
        label={t('verify.evidence')}
        value={evidence}
        rows={2}
        disabled={busy}
        hint={t('verify.evidenceHint')}
        error={noEvidence ? t('verify.evidenceRequired') : undefined}
        onChange={(event) => onEvidence(event.target.value)}
      />

      <fieldset className="verify__facts">
        <legend className="field__label">{t('verify.facts')}</legend>
        {VERIFICATION_FACTS.map((fact) => (
          <label key={fact} className="verify__fact">
            <input
              type="checkbox"
              checked={facts.includes(fact)}
              disabled={busy}
              onChange={() => onToggleFact(fact)}
            />
            {verificationFactLabel(fact)}
          </label>
        ))}
        {noFacts ? <span className="field__error">{t('verify.factsRequired')}</span> : null}
      </fieldset>

      <TextArea
        label={t('verify.note')}
        value={note}
        rows={2}
        disabled={busy}
        onChange={(event) => onNote(event.target.value)}
      />

      <p className="verify__ttl">{t('verify.ttlNote')}</p>

      <div className="draft__actions">
        {/*
          Disabled rather than refused after the fact: the two things this button
          insists on are the two things that make the stored check worth reading
          later, and finding that out from a server error is finding it out after
          the phone call has ended.
        */}
        <Button busy={busy} disabled={noEvidence || noFacts} onClick={onSubmit}>
          {busy ? t('verify.submitting') : t('verify.submit')}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  )
}
